import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { storage } from '../utils/storage';
import { en } from './locales/en';
import { hi } from './locales/hi';
import { id } from './locales/id';
import { ms } from './locales/ms';
import { zh } from './locales/zh';
import { translate } from './translate';
import { DATE_LOCALES, isLocale, type Locale } from './types';
import type { MessageKey } from './keys';
import type { MessageVars } from './types';

const UI_LANGUAGE_KEY = 'ui_language';

const catalogs = { en, id, hi, ms, zh } as const;

export type TranslateFn = (key: MessageKey, vars?: MessageVars) => string;

export interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslateFn;
  dateLocale: string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readStoredLocale(): Locale {
  const stored = storage.getItem(UI_LANGUAGE_KEY);
  return isLocale(stored) ? stored : 'en';
}

function applyDocumentLang(locale: Locale) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

function createTranslator(locale: Locale): TranslateFn {
  return (key, vars) => translate(catalogs[locale], catalogs.en, key, vars);
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  useEffect(() => {
    applyDocumentLang(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    storage.setItem(UI_LANGUAGE_KEY, next);
    applyDocumentLang(next);
  }, []);

  const t = useMemo(() => createTranslator(locale), [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t,
      dateLocale: DATE_LOCALES[locale],
    }),
    [locale, setLocale, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Falls back to English when rendered outside a provider (tests, isolated stories). */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (ctx) return ctx;
  return {
    locale: 'en',
    setLocale: () => undefined,
    t: createTranslator('en'),
    dateLocale: DATE_LOCALES.en,
  };
}
